import React, { useEffect, useState } from 'react';
import { Tabs, List, Avatar, Badge, Typography } from 'antd';
import { HeartOutlined, CalendarOutlined, HeartTwoTone } from '@ant-design/icons';
import Confetti from 'react-confetti';

const { Text } = Typography;

// Helper function to format date as "Month Day"
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};
//testhbg
// Helper function to extract first name
const getFirstName = (fullName) => {
  if (!fullName) return '';
  const cleanedName = fullName.replace(/^(Mr\.|Ms\.|Mrs\.)\s*/, '').replace(/\s*\([^)]+\)$/, '');
  const nameParts = cleanedName.trim().split(' ');
  return nameParts[0] || '';
};

// CSS styles for Marriage Anniversary
const marriageStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Dancing+Script:wght@400;700&display=swap');

  .marriage-card-container {
    perspective: 1000px;
    width: 100%;
    height: 60px;
  }

  .marriage-card {
    position: relative;
    width: 100%;
    height: 100%;
    transition: transform 0.6s;
    transform-style: preserve-3d;
  }

  .marriage-card:hover {
    transform: rotateY(180deg);
  }

  .marriage-card-front,
  .marriage-card-back {
    position: absolute;
    width: 100%;
    height: 100%;
    backface-visibility: hidden;
    display: flex;
    align-items: center;
    padding: 8px;
    border: 1px solid #e8e8e8;
    border-radius: 6px;
    box-sizing: border-box;
  }

  .marriage-card-front {
    background: linear-gradient(to right, #fff0f5, #f3e7f7);
  }

  .marriage-card-back {
    background: linear-gradient(to right, #ffccd5, #eb2f96);
    transform: rotateY(180deg);
    justify-content: center;
    align-items: center;
    color: white;
    font-family: 'Dancing Script', cursive;
    font-size: 16px;
    font-weight: 700;
    text-align: center;
    box-shadow: 0 0 10px rgba(235, 47, 150, 0.5);
  }

  .upcoming-card {
    transition: transform 0.3s ease;
    width: 100%;
    height: 60px;
    display: flex;
    align-items: center;
    padding: 8px;
    border: 1px solid #e8e8e8;
    border-radius: 6px;
    background: linear-gradient(to right, #f3e7f7, #fff0f5);
  }

  .upcoming-card:hover {
    transform: scale(1.05);
  }

  .marriage-glow {
    animation: heartGlow 1.5s ease-in-out infinite;
    box-shadow: 0 0 10px rgba(235, 47, 150, 0.5), 0 0 20px rgba(235, 47, 150, 0.3);
  }

  .pulse {
    animation: pulse 0.7s ease;
  }

  @keyframes heartGlow {
    0%, 100% {
      box-shadow: 0 0 10px rgba(235, 47, 150, 0.5), 0 0 20px rgba(235, 47, 150, 0.3);
    }
    50% {
      box-shadow: 0 0 20px rgba(235, 47, 150, 0.8), 0 0 30px rgba(235, 47, 150, 0.5);
    }
  }

  @keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.1); }
    100% { transform: scale(1); }
  }

  .heart-badge {
    position: absolute;
    top: -10px;
    right: -10px;
    background: #eb2f96;
    color: white;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    animation: heartbeat 1.5s ease-in-out infinite;
  }

  @keyframes heartbeat {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.2); }
  }
`;

// Inject styles
const styleSheet = document.createElement('style');
styleSheet.innerText = marriageStyles;
document.head.appendChild(styleSheet);

const MarriageAnniversary = ({ anniversary }) => {
  const { TodayMarriageAnniversary = [], UpcommingMarriageAnniversary = [] } = anniversary || {};
  const [showConfetti, setShowConfetti] = useState(false);
  const [defaultActiveKey, setDefaultActiveKey] = useState('today');
  const today = formatDate(new Date());

  // Function to check if tab has data
  const hasData = (tabKey) => {
    switch (tabKey) {
      case 'today':
        return TodayMarriageAnniversary && TodayMarriageAnniversary.length > 0;
      case 'upcoming':
        return UpcommingMarriageAnniversary && UpcommingMarriageAnniversary.length > 0;
      default:
        return false;
    }
  };

  // Effect to determine the default active tab based on available data
  useEffect(() => {
    if (hasData('today')) {
      setDefaultActiveKey('today');
    } else if (hasData('upcoming')) {
      setDefaultActiveKey('upcoming');
    } else {
      setDefaultActiveKey('today');
    }
  }, [TodayMarriageAnniversary, UpcommingMarriageAnniversary]);

  // Check for today's anniversaries
  useEffect(() => {
    const hasAnniversaryToday = TodayMarriageAnniversary.some(
      (person) => formatDate(person.MarriageAnniversary) === today
    );
    if (hasAnniversaryToday) {
      setShowConfetti(true);
      const timer = setTimeout(() => setShowConfetti(false), 6000);
      return () => clearTimeout(timer);
    }
  }, [TodayMarriageAnniversary, today]);

  const items = [
    {
      key: 'today',
      label: (
        <div className="flex items-center gap-1">
          <HeartTwoTone twoToneColor="#eb2f96" />
          <Text strong style={{ fontSize: '14px', color: '#1f1f1f' }}>
            Today
          </Text>
          <Badge
            count={TodayMarriageAnniversary.length}
            style={{ backgroundColor: '#eb2f96', fontSize: '10px', height: '16px', lineHeight: '16px' }}
          />
        </div>
      ),
      children: (
        <div className="p-2 relative">
          {showConfetti && (
            <Confetti
              width={window.innerWidth}
              height={window.innerHeight}
              numberOfPieces={100}
              recycle={false}
              gravity={0.15}
              colors={['#eb2f96', '#ff85a2', '#ffccd5', '#fff0f5']}
            />
          )}
          <List
            grid={{ gutter: 8, column: 2, xs: 1, sm: 2 }}
            dataSource={TodayMarriageAnniversary}
            locale={{ emptyText: 'No anniversaries today 💕' }}
            renderItem={(person) => (
              <List.Item>
                <div className="marriage-card-container">
                  <div
                    className={`marriage-card ${
                      formatDate(person.MarriageAnniversary) === today ? 'marriage-glow pulse' : ''
                    }`}
                  >
                    <div className="marriage-card-front">
                      <Avatar
                        size={32}
                        style={{ backgroundColor: '#eb2f96', marginRight: '8px' }}
                      >
                        {person.EmployeeName?.charAt(0)}
                      </Avatar>
                      <div>
                        <Text strong style={{ fontSize: '12px', color: '#1f1f1f' }} className="truncate">
                          {person.EmployeeName}
                        </Text>
                        <div className="flex items-center gap-1">
                          <HeartOutlined style={{ color: '#eb2f96', fontSize: '12px' }} />
                          <Text type="secondary" style={{ fontSize: '10px' }}>
                            {formatDate(person.MarriageAnniversary)}
                          </Text>
                        </div>
                      </div>
                      {formatDate(person.MarriageAnniversary) === today && (
                        <span className="heart-badge">💖</span>
                      )}
                    </div>
                    <div className="marriage-card-back">
                      Happy Anniversary {getFirstName(person.EmployeeName)}!
                    </div>
                  </div>
                </div>
              </List.Item>
            )}
          />
        </div>
      ),
    },
    {
      key: 'upcoming',
      label: (
        <div className="flex items-center gap-1">
          <CalendarOutlined style={{ color: '#722ed1' }} />
          <Text strong style={{ fontSize: '14px', color: '#1f1f1f' }}>
            Upcoming
          </Text>
          <Badge
            count={UpcommingMarriageAnniversary.length}
            style={{ backgroundColor: '#722ed1', fontSize: '10px', height: '16px', lineHeight: '16px' }}
          />
        </div>
      ),
      children: (
        <div className="p-2">
          <List
            grid={{ gutter: 8, column: 2, xs: 1, sm: 2 }}
            dataSource={UpcommingMarriageAnniversary}
            locale={{ emptyText: 'No upcoming anniversaries 💍' }}
            renderItem={(person) => (
              <List.Item>
                <div className="upcoming-card">
                  <Avatar
                    size={32}
                    style={{ backgroundColor: '#722ed1', marginRight: '8px' }}
                  >
                    {person.EmployeeName?.charAt(0)}
                  </Avatar>
                  <div>
                    <Text strong style={{ fontSize: '12px', color: '#1f1f1f' }} className="truncate">
                      {person.EmployeeName}
                    </Text>
                    <div className="flex items-center gap-1">
                      <CalendarOutlined style={{ color: '#722ed1', fontSize: '12px' }} />
                      <Text type="secondary" style={{ fontSize: '10px' }}>
                        {formatDate(person.JoinDate)}
                      </Text>
                    </div>
                  </div>
                </div>
              </List.Item>
            )}
          />
        </div>
      ),
    },
  ];

  return (
    <div
      className="marriage-bar rounded-lg shadow-lg"
      style={{ border: '1px solid var(--bg-column)' }}
    >
      <div className="flex items-center justify-center gap-0 p-2 bg-gradient-to-r from-pink-50 to-purple-50 rounded-t-lg">
        <HeartTwoTone twoToneColor="#eb2f96" style={{ fontSize: '16px' }} />
        <Text strong style={{ color: '#1f1f1f', fontSize: '14px' }}>
          Marriage Anniversaries
        </Text>
      </div>
      <Tabs
        defaultActiveKey={defaultActiveKey}
        key={defaultActiveKey}
        items={items}
        tabBarStyle={{ margin: 0, padding: '0 12px', background: '#fafafa' }}
        animated={{ inkBar: true, tabPane: false }}
      />
    </div>
  );
};

export default MarriageAnniversary;